import React, {Component} from 'react';
import './App.css';
import {Link, withRouter} from 'react-router-dom';
import {getInterestZip} from './interest';

const RECENT_FETCH_LIMIT = 50;
const RECENT_DISPLAY_COUNT = 6;
const RECENT_CACHE_KEY = 'home_recentTransactionsCache';

function loadRecentTransactionsCache() {
  const cached = window.sessionStorage.getItem(RECENT_CACHE_KEY);
  return cached ? JSON.parse(cached) : null;
}

function saveRecentTransactionsCache(cache) {
  window.sessionStorage.setItem(RECENT_CACHE_KEY, JSON.stringify(cache));
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

function shuffle(items) {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const RecentTransactionCard = ({transaction}) => (
  <Link className="recent-card" to={`/transactions?propertyId=${transaction.attomId}`}>
    <div className="recent-card-price">{currencyFormatter.format(transaction.transferAmount)}</div>
    <div className="recent-card-address">{transaction.propertyAddressFull}</div>
    <div className="recent-card-meta">
      {transaction.propertyAddressCity}, {transaction.propertyAddressState} {transaction.propertyAddressZIP}
    </div>
    <div className="recent-card-date">{new Date(transaction.recordingDate).toLocaleDateString()}</div>
  </Link>
);

class Home extends Component {

  state = {
    recentTransactions: [],
    isLoadingRecent: true,
    recentZip: null
  };

  componentDidMount() {
    const cached = loadRecentTransactionsCache();
    if (cached) {
      this.setState({
        recentTransactions: cached.recentTransactions,
        isLoadingRecent: false,
        recentZip: cached.recentZip
      });
    } else {
      this.fetchRecentTransactions();
    }
  }

  async fetchRecentTransactions() {
    const zip = getInterestZip();
    let jsonData = await this.fetchRecent(zip);
    let appliedZip = zip;
    if (zip && jsonData.length === 0) {
      jsonData = await this.fetchRecent(null);
      appliedZip = null;
    }
    const recentTransactions = shuffle(jsonData).slice(0, RECENT_DISPLAY_COUNT);
    this.setState({
      recentTransactions,
      isLoadingRecent: false,
      recentZip: appliedZip
    });
    saveRecentTransactionsCache({recentTransactions, recentZip: appliedZip});
  }

  async fetchRecent(zip) {
    const response = await this.props.transactionsApi.recent(RECENT_FETCH_LIMIT, zip);
    return response.ok ? await response.json() : [];
  }

  render() {
    return (
      <div className="app">
        {this.props.navbar}

        <div className="hero">
          <div className="hero-inner">
            <span className="hero-eyebrow">Bay Area property records</span>

            <h1 className="hero-title">
              Search real estate transactions <span className="accent">across the Bay Area</span>
            </h1>

            <p className="hero-subtitle">
              Look up ownership and sale history by name or address. Transaction records
              go back to the 1970s across Santa Clara, Alameda, and Marin counties.
            </p>

            <div className="hero-actions">
              <Link className="hero-action-card" to="/person">
                <span className="hero-action-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
                    <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </span>
                <span>
                  <div className="hero-action-title">Search by name</div>
                  <div className="hero-action-desc">Find properties tied to a person</div>
                </span>
              </Link>

              <Link className="hero-action-card" to="/property">
                <span className="hero-action-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M4 11l8-6 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 10v9h12v-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 19v-5h4v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span>
                  <div className="hero-action-title">Search by address</div>
                  <div className="hero-action-desc">Look up a property's history</div>
                </span>
              </Link>
            </div>

            <div className="hero-stats">
              <div>
                <div className="hero-stat-value">3</div>
                <div className="hero-stat-label">counties covered</div>
              </div>
              <div>
                <div className="hero-stat-value">1970s</div>
                <div className="hero-stat-label">records go back to</div>
              </div>
              <div>
                <div className="hero-stat-value">More</div>
                <div className="hero-stat-label">counties coming soon</div>
              </div>
            </div>
          </div>
        </div>

        <div className="recent-section">
          <div className="recent-inner">
            <span className="page-eyebrow">Live activity</span>
            <h2 className="recent-title">Recent transactions</h2>
            <p className="recent-subtitle">
              {this.state.recentZip
                ? `Recent sales near ZIP ${this.state.recentZip}, based on your recent searches.`
                : 'A random sample of recent sales across our coverage area.'}
            </p>

            {this.state.isLoadingRecent &&
              <div className="loading-row"><span className="spinner"/> Loading recent transactions...</div>
            }

            {!this.state.isLoadingRecent && this.state.recentTransactions.length === 0 &&
              <div className="alert callout callout-neutral">No recent transactions available right now</div>
            }

            {this.state.recentTransactions.length > 0 &&
              <div className="recent-grid">
                {this.state.recentTransactions.map((transaction, index) => (
                  <RecentTransactionCard
                    key={transaction.transactionId || index}
                    transaction={transaction}
                  />
                ))}
              </div>
            }
          </div>
        </div>

        <div className="site-footer">
          SoReal &middot; Santa Clara, Alameda &amp; Marin counties &middot; <Link className="app-link" to="/about">About</Link>
        </div>
      </div>
    );
  }
}

export default withRouter(Home);
